import React from "react";
import { motion } from "framer-motion";
import {
  TestTube2,
  ArrowUpRight,
  Zap,
  Shield,
  RefreshCw,
  Brain,
  Clock,
  TrendingUp,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const aiCapabilities = [
  {
    icon: Brain,
    title: "AI Test Case Generation",
    description:
      "Generate test cases from your requirements and flows using AI — with configurable prompts and support for OpenAI and Google GenAI.",
  },
  {
    icon: RefreshCw,
    title: "Automated Test Execution",
    description:
      "Run automated test launches with step-by-step execution, variable-based execution plans, and detailed result tracking.",
  },
  {
    icon: Shield,
    title: "Test Groups & Organization",
    description:
      "Organize test cases with modules, labels, and test groups. Run manual or automated test runs and track history per launch.",
  },
  {
    icon: TrendingUp,
    title: "Execution Results & Analytics",
    description:
      "View detailed execution results for every test run and launch, with tracking and analytics to keep releases on track.",
  },
  {
    icon: Clock,
    title: "Bulk Import & Export",
    description:
      "Import and export test cases via Excel. Onboard large test suites quickly and keep test data in sync across teams.",
  },
  {
    icon: Zap,
    title: "Test Launch Management",
    description:
      "Create test launches with execution plans and variables. Run coordinated test runs and track status and results per launch.",
  },
];

const stats = [
  { value: "AI-driven", label: "Test case generation" },
  { value: "Structured", label: "Execution plans" },
  { value: "Tracked", label: "Results & history" },
  { value: "Organized", label: "Groups & modules" },
];

const Products = () => {
  const navigate = useNavigate();

  const goToSignup = () => navigate("/signup");

  return (
    <section
      id="products"
      className="section-padding section-divider relative overflow-hidden bg-slate-50"
    >
      {/* Background accent */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/5 rounded-full blur-[150px] pointer-events-none" />

      <div className="container mx-auto relative">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-20"
        >
          <span className="inline-flex items-center gap-2 text-[11px] font-semibold tracking-[0.2em] uppercase text-primary/90 mb-5 border border-primary/15 rounded-full px-4 py-1.5 bg-primary/5">
            <TestTube2 size={11} />
            QA Path
          </span>
          <h2 className="text-3xl md:text-5xl lg:text-6xl font-display font-bold mb-6 tracking-[-0.02em]">
            AI-Powered Test Automation
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto mb-10 leading-relaxed font-light">
            Let AI write, execute, and manage your test cases — from smoke tests
            to full regression suites. QA Path learns your application and keeps
            your releases rock-solid.
          </p>
          <button
            type="button"
            onClick={goToSignup}
            className="group inline-flex items-center gap-2.5 px-8 py-3.5 rounded-xl bg-primary text-primary-foreground font-semibold text-sm hover:opacity-90 transition-all duration-300 shadow-lg shadow-primary/25"
          >
            <TestTube2 size={15} />
            Start Free Trial
            <ArrowUpRight
              size={13}
              className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300"
            />
          </button>
        </motion.div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto mb-28">
          {stats.map((stat, si) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: si * 0.08 }}
              className="glass rounded-2xl p-6 text-center card-hover gradient-border"
            >
              <div className="text-3xl md:text-4xl font-display font-bold text-gradient mb-1.5">
                {stat.value}
              </div>
              <div className="text-xs text-muted-foreground font-medium tracking-wide">
                {stat.label}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Capabilities */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h3 className="text-2xl md:text-4xl font-display font-bold mb-4 tracking-[-0.01em]">
            How AI Transforms Your QA
          </h3>
          <p className="text-muted-foreground max-w-xl mx-auto leading-relaxed font-light">
            From AI-generated test cases to automated launches and results
            tracking — so your team can focus on building.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 max-w-6xl mx-auto">
          {aiCapabilities.map((cap, i) => (
            <motion.div
              key={cap.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.06 }}
              className="group glass rounded-2xl p-8 space-y-5 card-hover"
            >
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors duration-500">
                <cap.icon size={21} className="text-primary" />
              </div>
              <h4 className="font-display font-bold text-lg text-foreground tracking-tight group-hover:text-primary transition-colors duration-300">
                {cap.title}
              </h4>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {cap.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Products;

